import { createContext, FunctionComponent, useEffect, useState } from "react";
import CollectionLongForm from "./collectionLongForm/CollectionLongForm";
import CollectionTableForm from "./collectionTableForm/CollectionTableForm";
import { JSONSchema7 } from "json-schema";
import { IFormSchemaResponse, NativeId, PrimaryKeyWithDate } from "data/types";
import { Alert, AlertColor, Button, IconButton, Snackbar, Stack, ToggleButton, ToggleButtonGroup } from "@mui/material";
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { FormStatus, IMonitorForm, makeBlankForm, makeCloneForm, wrapExistingForm } from "data/formStatus";
import useCartaObjectStorage from "hooks/useCartaObjectStorage";
import CartaObjectStorage from "data/CartaObjectStorage";
import { FormStatusIcon } from "./collectionLongForm/components/FormStatusIcon";
import { timeout } from "library/util";
import { useHistory } from "react-router";
import { useProjectErrors } from "hooks/useProjectErrors";

export {CollectionLongForm, CollectionTableForm};

interface FormEntryContextProps {
    primaryKeys: PrimaryKeyWithDate[];
    projectId: string;
}


interface FormSaveNotification{
    severity: AlertColor;
    message: string;
  }
  

enum FormType{
  Long = "long",
  Table = "table"
}
  
export const FormEntryContext = createContext<FormEntryContextProps | null>(null);


function stripImageFields(data: any, schema: JSONSchema7): any {
  if (!data || typeof data !== 'object' || !schema.properties) return data;
  const result: any = {};
  for (const [key, value] of Object.entries(data)) {
    const propSchema = schema.properties[key];
    if (!propSchema || typeof propSchema === 'boolean') {
      result[key] = value;
      continue;
    }
    if ((propSchema as any)['cl-format'] === 'image') continue;
    result[key] = (propSchema.type === 'object')
      ? stripImageFields(value, propSchema)
      : value;
  }
  return result;
}

async function saveForms(cos: CartaObjectStorage, forms: IMonitorForm[], schemaNative: NativeId, projectId: string, schema: JSONSchema7, connectionId?: string){
    // A brand-new form (opened via schema key, with no connectionId yet known) doesn't
    // know which connection it landed in until it's created; capture that from the
    // first creation response so the caller can promote the URL to the "existing form"
    // shape once every form here is guaranteed to share the same connection.
    let resolvedConnectionId = connectionId;

    const saveRequests = forms.map(async (form, index)=>{
      try{
        if(!form.data){
          console.error(`Nothing to save at form ${index}!`)
          return false;
        }

        const formData = stripImageFields(form.data, schema);

        if(form.nativeId){
          const saveResponse = await cos.saveCollection({
            projectId, connectionId, formData, nativeId: form.nativeId, key: schemaNative
          })
          // The saved nativeId can differ from the one submitted (e.g. a version
          // bump), so the URL must be rebuilt from what the server now considers
          // this form's identity, not the one the request was made with.
          if(saveResponse[0]?.node){
            form.nativeId = saveResponse[0].node;
          }
        }else{
          const collection = await cos.createCollection({
            projectId, key: schemaNative, formData
          })

          form.nativeId = collection[0].nativeId
          if(!resolvedConnectionId && collection[0].connectionId){
            resolvedConnectionId = collection[0].connectionId;
          }
        }
        form.status = FormStatus.Saved;
        return true;
      }catch(e){
        console.error(`Error saving form ${index}`, e)
        form.isError = true;
        return false;
      }
    })

    const results = await Promise.all(saveRequests);
    return { results, connectionId: resolvedConnectionId };
  }

/**
 * Rewrites the address bar to the "load existing form(s)" shape (connectionId +
 * one nativeId per form) without triggering a react-router navigation. A form
 * that was opened via schema key (create-new) or as a single node otherwise has
 * no way to know, on refresh or bookmark, that it's since been saved and should
 * reopen as existing rather than create another copy.
 */
function updateUrlToReflectSavedForms(connectionId: string | undefined, forms: IMonitorForm[]){
    const nativeIds = forms.map(form => form.nativeId).filter((nativeId): nativeId is NativeId => !!nativeId);
    if(!connectionId || nativeIds.length === 0){
      return;
    }

    const params = new URLSearchParams();
    params.set("connectionId", connectionId);
    nativeIds.forEach(nativeId => params.append("nativeId", JSON.stringify(nativeId)));

    const newUrl = `${window.location.pathname}?${params.toString()}`;
    window.history.replaceState(window.history.state, "", newUrl);
  }

interface CollectionFormProps {
    projectId: string;
    schema?: IFormSchemaResponse | null;
    connectionId?: string;
  }

export const CollectionForm: FunctionComponent<CollectionFormProps> = ({
    schema,
    projectId,
    connectionId
})=>{
    const [alertStatus, setAlertStatus] = useState<FormSaveNotification | null>(null);
    const [viewMode, setViewMode] = useState<FormType>(FormType.Long);
    // A form opened via schema key (create-new) starts with no known connectionId.
    // Track whatever the first successful creation resolves it to, since the
    // connectionId prop itself won't change without a page reload, and later
    // saves of that same form need it to update rather than re-create.
    const [effectiveConnectionId, setEffectiveConnectionId] = useState<string | undefined>(connectionId);
    const [forms, setForms] = useState<IMonitorForm[]>([]);
    const history = useHistory();
    const projectErrors = useProjectErrors();

    useEffect(()=>{
        setEffectiveConnectionId(connectionId);
    }, [connectionId])

    useEffect(()=>{
        if(schema){
            setForms(schema.form_data?.map(form=>wrapExistingForm(form.data, form.nativeId)) ?? [makeBlankForm()]); // Initialize with one empty form
        }else{
            setForms([])
        }
    }, [schema])
    
  const cartaObjectStorage = useCartaObjectStorage();

  const closeFormSaveNotification = ()=>{
    setAlertStatus(null);
  }

  
  const handleFormAddition = () => {
      setForms([...forms, makeBlankForm()]);
  };

  const handleFormDuplication = (index: number) => {
      const formToDuplicate = forms[index];
      setForms([...forms, makeCloneForm(formToDuplicate)]);
  };

  const handleFormRemoval = (index: number) => {
    const updatedForms = forms.filter((_, i) => i !== index);
    setForms(updatedForms);
  };

  const handleFormChange = (index: number, updatedData: any, hasError: boolean, isBootstrapChange?: boolean) => {
    const updatedForms = [...forms];
    const updatedForm = updatedForms[index];
    if(!isBootstrapChange && updatedForm.status === FormStatus.Saved && typeof updatedForm.data !== 'undefined'){
      updatedForm.status = FormStatus.Modified;
    }
    updatedForm.isError = hasError;
    updatedForm.data = updatedData;
    setForms(updatedForms);
  };

  const handleSave = async () => {
    try {
      if(!cartaObjectStorage){
        throw new Error("Unable to load Carta Object Storage");
      }
      if (!schema) {
        throw new Error("Attempting to save when no schema defined");
      }
      setAlertStatus({
        severity: "info",
        message: "Saving forms..."
      });
      const schemaKey = { id: schema.schema_key.id, version: schema.schema_key.version }

      const { results: saveSuccess, connectionId: resolvedConnectionId } =
        await saveForms(cartaObjectStorage, forms, schemaKey, projectId, schema.schema as JSONSchema7, effectiveConnectionId);
      setForms([...forms]);
      if(resolvedConnectionId && resolvedConnectionId !== effectiveConnectionId){
        setEffectiveConnectionId(resolvedConnectionId);
      }
      updateUrlToReflectSavedForms(resolvedConnectionId, forms);

      let successes: number = 0;
      for(let i = 0; i < saveSuccess.length; i++){
        successes += saveSuccess[i] ? 1 : 0;
      }

      if(successes > 0){
        // A save may have triggered Post Submit Targets that fail asynchronously;
        // start (or restart) the error-polling window for this project.
        projectErrors.notifySaved();
      }

      if(successes === saveSuccess.length){
        setAlertStatus({
          severity: "success",
          message: "All forms saved successfully"
        });
        return true;
      }else if(successes === 0){
        setAlertStatus({
          severity: "error",
          message: "Failed to save forms"
        });
        return false;
      }else{
        setAlertStatus({
          severity: "warning",
          message: `${successes}/${saveSuccess.length} forms saved successfully`
        });
        return false;
      }

    } catch (error) {
      console.error("Error saving forms:", error);
      setAlertStatus({
        severity: "error",
        message: "An error occurred while saving the forms. Please try again."
      });
      return false;
    }
  };

  const handleSaveAndClose = async ()=>{
    const success = await handleSave();
    if(success){
      await timeout(200);
      history.push(`/${projectId}`)
    }
  }

  const handleViewToggle = (event: React.MouseEvent<HTMLElement>, newViewMode: FormType | null) => {
    if (newViewMode) {
      setViewMode(newViewMode);
    }
  };

  if(!schema){
    return <>Loading...</>
  }

    return (
        <>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewToggle}
            aria-label="View mode"
            style={{ marginBottom: "1rem" }}>
            {Object.entries(FormType).map(([key, value]) => {
              return (
                <ToggleButton value={value} key={key} aria-label={`${key} form view`}>
                  {`${key} form view`}
                </ToggleButton>
              )
            })}
          </ToggleButtonGroup>
          <IconButton
            aria-label="add blank form"
            onClick={handleFormAddition}
            style={{ backgroundColor: "#28a745", color: "white", marginLeft: "1rem" }}>
            <AddIcon />
          </IconButton>
        </div>
        <FormEntryContext.Provider value={schema ? { primaryKeys: schema.primaryKeys, projectId: projectId } : null}>
          {/* Render either the long form view or the table view */}
          {viewMode === FormType.Long
            ? schema &&
              forms.map((form, index) => (
                <Accordion key={index} style={{ marginBottom: "1rem" }} defaultExpanded={index === 0}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}-content`}
                    id={`panel${index}-header`}>
                    <Typography variant="h6" style={{display: 'flex', alignItems: 'center', gap: 2}}> 
                      <FormStatusIcon status={form.status} isError={form.isError}/>
                      <span>Form {index + 1}</span>
                    </Typography>
                    <div style={{ marginLeft: "auto" }}>
                      <IconButton
                        aria-label="duplicate form"
                        onClick={(event) => {
                          event.stopPropagation();
                          handleFormDuplication(index)
                        }}
                        color="primary">
                        <ContentCopyIcon />
                      </IconButton>
                      <IconButton aria-label="remove form" onClick={(event) => {
                          event.stopPropagation();
                          handleFormRemoval(index);
                          }}
                          color="secondary">
                        <DeleteIcon />
                      </IconButton>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    <CollectionLongForm
                      formData={form}
                      schema={schema.schema as JSONSchema7}
                      onChange={(data, hasError, isBootstrapChange) => handleFormChange(index, data, hasError, isBootstrapChange)}
                    />
                  </AccordionDetails>
                </Accordion>
              ))
            : schema && (
                <CollectionTableForm
                  forms={forms}
                  schema={schema.schema}
                  onChange={(index, updatedForm, hasError) => handleFormChange(index, updatedForm, hasError)}
                  onDuplicate={handleFormDuplication}
                  onRemove={handleFormRemoval}
                />
              )}
        </FormEntryContext.Provider>

        <Stack spacing={2} direction="row" justifyContent={"end"}>
          <Button
            onClick={handleSave}
            style={{ marginTop: "1rem", backgroundColor: "#a6a6a6", color: "#fff" }}
            disabled={forms.length === 0}>
            Save
          </Button>
          <Button
            onClick={handleSaveAndClose}
            style={{ marginTop: "1rem", backgroundColor: "#28a745", color: "#fff" }}
            disabled={forms.length === 0}>
            Save and Close
          </Button>
        </Stack>
        <Snackbar open={!!alertStatus} autoHideDuration={6000} onClose={closeFormSaveNotification}>
          <Alert
            onClose={closeFormSaveNotification}
            severity={alertStatus?.severity}
            variant="filled"
            sx={{ width: '100%' }}
          >
            {alertStatus?.message}
          </Alert>
        </Snackbar>
        </>
    )
}